// Seeding a quote form from an existing offer, for the Revise flow.
//
// The guarantee is that the user re-enters nothing: every input the
// original quote was built from is read back out of the offer and pushed
// into the form's state. The user then changes only what actually changed
// and saves, and the premium is recomputed server-side from the edited
// inputs — a revision is re-quoted, never copied.
//
// Offers store their inputs in two places:
//   params.policyholder   — the form's policyholder block, verbatim
//   params.productConfig  — the product inputs, as normalised at create
// and the computed result in params.calculation. For inputs that older
// offers didn't persist under productConfig (the RC Pro personnel roster,
// the PGM employee roster) we reconstruct from params.calculation, which
// has always carried enough detail. That is what makes Revise work on
// offers created long before this feature existed, with no migration.

import { normalisePaymentFrequency } from '../../utils/paymentTerms.js';
import { asObjectId } from '../../utils/references.js';

let seedRowIdCounter = 0;
const seedRowId = (prefix) => `${prefix}_seed_${++seedRowIdCounter}`;

const str = (v) => (v == null ? '' : String(v));
const dateOnly = (v) => (v ? str(v).slice(0, 10) : '');

/** Common bits every product form shares. */
export function seedCommon(offer) {
  const params = offer?.params || {};
  return {
    policyholder: params.policyholder || null,
    productConfig: params.productConfig || {},
    calculation: params.calculation || {},
    // IDS ONLY. Both are posted back as references, so a value that is not an
    // ObjectId — the list's display dash for "no broker", or a broker NAME —
    // must seed '' (no selection), never itself. It used to fall back to
    // `offer.broker`, which the list had set to "—" for a broker-less offer,
    // and every revision of one failed with "Invalid _id: —." (CH, 2026-09-28).
    clientId: asObjectId(offer?.clientId),
    brokerId: asObjectId(offer?.brokerId) || asObjectId(offer?.broker),
    notes: str(offer?.notes || ''),
  };
}

/** PGMA: single insured person, one salary. */
export function seedPgma(offer) {
  const { policyholder, productConfig, calculation, clientId, brokerId } = seedCommon(offer);
  const inputs = calculation.inputs || {};
  return {
    clientId,
    brokerId,
    policyholder: policyholder ? {
      companyName: str(policyholder.companyName),
      address:     str(policyholder.address),
      phone:       str(policyholder.phone),
      email:       str(policyholder.email),
      branch:      str(policyholder.branch || policyholder.industry),
      lastName:    str(policyholder.lastName),
      firstName:   str(policyholder.firstName),
      dateOfBirth: dateOnly(policyholder.dateOfBirth),
      gender:      str(policyholder.gender),
    } : null,
    productConfig: {
      // Prefer the stored productConfig, fall back to what the engine
      // echoed back. `annualSalary` is the requested figure, not the
      // capped one — re-seeding the cap would quietly lower the quote.
      annualSalary:  str(productConfig.annualSalary ?? inputs.annualSalary ?? ''),
      coverageType:  str(productConfig.coverageType ?? inputs.coverageType ?? 'maladie_accident'),
      waitingPeriod: str(productConfig.waitingPeriod ?? inputs.waitingPeriod ?? '30'),
      effectiveDate: dateOnly(productConfig.effectiveDate || inputs.effectiveDate),
    },
  };
}

// Activity round-trip. Offers persist the canonical 0-1 decimal; the form
// edits a percentage. A revision must reproduce the SAME premium, so the
// stored value is converted rather than re-defaulted — falling back to
// full-time here would quietly re-price a part-time doctor at 100%.
function seedActivityPercent(activityFTE) {
  const fte = Number(activityFTE);
  if (!Number.isFinite(fte) || fte <= 0) return '100';
  return String(Math.round(fte * 100 * 100) / 100);
}

// First integer among the candidates, else null.
function firstIndex(...candidates) {
  for (const c of candidates) if (Number.isInteger(c)) return c;
  return null;
}

/** RC Pro: company plus a personnel roster of specialisations. */
export function seedRcPro(offer) {
  const { policyholder, productConfig, calculation, clientId, brokerId } = seedCommon(offer);
  const inputs = calculation.inputs || {};

  // Roster: use the stored one when present. Otherwise rebuild from the
  // computed line items, which carry the specialisation code, FTE and the
  // assistant/holding relationships. surchargeLines recovers the
  // non-indicated flag via linkedPersonnelIndex.
  const surchargedIdx = new Set(
    (calculation.surchargeLines || [])
      .map(l => l.linkedPersonnelIndex)
      .filter(i => Number.isInteger(i)),
  );
  const stored = Array.isArray(productConfig.personnel) ? productConfig.personnel : [];
  const rows = stored.length
    ? stored.map(r => ({
        id: seedRowId('rcpro'),
        personName:            str(r.personName),
        specialization:        str(r.specialization),
        activityPercent:       seedActivityPercent(r.activityFTE),
        customLabel:           str(r.customLabel),
        customRate:            str(r.customRate),
        isAssistant:           Boolean(r.isAssistant),
        holdingRowId:          '',
        // Stored rows carry `holdingRowIndex` (the submitted field name);
        // line items carry `holdingIndex` (the engine's output name).
        // Reading only the latter here silently dropped the assistant's
        // link on every revision, leaving an unlinked assistant row.
        _holdingIndex:         firstIndex(r.holdingRowIndex, r.holdingIndex),
        nonIndicatedOver10Pct: Boolean(r.nonIndicatedOver10Pct),
      }))
    : (calculation.lineItems || []).map((li, i) => ({
        id: seedRowId('rcpro'),
        // Line items carry the name too, so a revision of an offer whose stored
        // roster predates the field still recovers whatever was captured.
        personName:            str(li.personName),
        specialization:        str(li.specialization),
        activityPercent:       seedActivityPercent(li.activityFTE),
        customLabel:           li.isCustom ? str(li.label) : '',
        customRate:            li.isCustom ? str(li.baseRate) : '',
        isAssistant:           Boolean(li.isAssistant),
        holdingRowId:          '',
        _holdingIndex:         firstIndex(li.holdingIndex, li.holdingRowIndex),
        nonIndicatedOver10Pct: surchargedIdx.has(i),
      }));

  // holdingIndex is positional; now that every row has an id, translate
  // the indices into ids so reordering the roster can't corrupt the links.
  const withHolding = rows.map(r => ({
    ...r,
    holdingRowId: r._holdingIndex != null && rows[r._holdingIndex] ? rows[r._holdingIndex].id : '',
  })).map(({ _holdingIndex, ...rest }) => rest);

  return {
    clientId,
    brokerId,
    policyholder: policyholder ? {
      companyName:  str(policyholder.companyName),
      address:      str(policyholder.address),
      canton:       str(policyholder.canton),
      contactName:  str(policyholder.contactName),
      contactEmail: str(policyholder.contactEmail),
      contactPhone: str(policyholder.contactPhone),
    } : null,
    productConfig: {
      sumInsured:      str(productConfig.sumInsured ?? inputs.sumInsured ?? ''),
      discountPercent: str(productConfig.discountPercent ?? inputs.discountPercent ?? '0'),
      effectiveDate:   dateOnly(productConfig.effectiveDate),
    },
    personnel: withHolding.length ? withHolding : null,
  };
}

/** PGM: company plus an employee roster. */
export function seedPgm(offer) {
  const { policyholder, productConfig, calculation, clientId, brokerId } = seedCommon(offer);
  const inputs = calculation.inputs || {};

  // The roster only ever lived on the calculation, as employeeDetails.
  // `annualSalary` is the requested figure; cappedSalary is derived and
  // must not be seeded back or the revision silently lowers the payroll.
  const rows = (calculation.employeeDetails || []).map(e => {
    const tokens = str(e.name).trim().split(/\s+/).filter(Boolean);
    return {
      id: seedRowId('pgm'),
      // employeeDetails stores a composed "prenom nom"; split it back on
      // the same convention the bulk parser uses.
      prenom:       tokens.length > 1 ? tokens[0] : '',
      nom:          tokens.length > 1 ? tokens.slice(1).join(' ') : (tokens[0] || ''),
      dateOfBirth:  dateOnly(e.dateOfBirth),
      gender:       str(e.gender),
      annualSalary: str(e.annualSalary),
    };
  });

  return {
    clientId,
    brokerId,
    policyholder: policyholder ? { ...policyholder } : null,
    productConfig: {
      waitingPeriod:     str(productConfig.waitingPeriod ?? inputs.waitingPeriod ?? ''),
      coveragePercent:   str(productConfig.coveragePercent ?? inputs.coveragePercent ?? ''),
      effectiveDate:     dateOnly(productConfig.effectiveDate || inputs.effectiveDate),
      // Codes, not display strings. A revision of an offer written before
      // the vocabulary existed rehydrates its legacy French label back to a
      // code; '' lets the form fall through to the tenant default rather
      // than seeding a frequency the tenant may no longer permit.
      paymentFrequency:  normalisePaymentFrequency(productConfig.paymentFrequency),
      // Legacy offers stored the French display string ('1 an'); pull the
      // leading number out so they still seed a valid term.
      contractTermYears: productConfig.contractTermYears != null
        ? Number(productConfig.contractTermYears)
        : (parseInt(str(productConfig.contractDuration), 10) || null),
    },
    employees: rows.length ? rows : null,
  };
}
